import * as THREE from 'three/webgpu';
import { solveTwoBone } from '../anim/ik';
import type { V3 } from '../sim/vec';

/**
 * Procedural pose layer — the control-rig equivalent. Runs AFTER the mixer each frame, editing
 * bones on top of the blended animation:
 *  - additive spine lean into acceleration / bank into turns (always on)
 *  - two-bone hand IK toward world-space targets (API ready; the parkour pass feeds it ledge
 *    points, interaction can feed it buttons/props)
 * Rule of the layer: it never blocks or delays physics — it only decorates the pose.
 */
type Side = 'left' | 'right';

interface ArmChain {
  root: THREE.Object3D; // Arm
  mid: THREE.Object3D; // ForeArm
  end: THREE.Object3D; // Hand
  lenA: number;
  lenB: number;
}

const V = () => new THREE.Vector3();

// Scratch for the spine decoration — one allocation for the module, not one per frame.
const LEAN_EULER = new THREE.Euler();
const LEAN_Q = new THREE.Quaternion();
/** Two unit quaternions are the same rotation when |dot| ≈ 1 (q and −q are equal rotations). */
const SAME_ROTATION_DOT = 1 - 1e-6;

export class PoseController {
  private spine: THREE.Object3D | null = null;
  private arms: Partial<Record<Side, ArmChain>> = {};
  private handTargets: Partial<Record<Side, { pos: THREE.Vector3; blend: number }>> = {};
  private smoothedAccel = new THREE.Vector2();
  private prevVel = new THREE.Vector2();
  /** The animated (mixer-written) spine pose this frame's lean/bank decorates. */
  private spineBase = new THREE.Quaternion();
  /** Exactly what we last WROTE to the spine — how we tell our own decoration from a fresh pose. */
  private spineApplied = new THREE.Quaternion();
  private spineDecorated = false;

  bind(root: THREE.Object3D): void {
    this.spine = root.getObjectByName('Spine1') ?? null;
    for (const side of ['left', 'right'] as const) {
      const prefix = side === 'left' ? 'Left' : 'Right';
      const a = root.getObjectByName(`${prefix}Arm`);
      const b = root.getObjectByName(`${prefix}ForeArm`);
      const c = root.getObjectByName(`${prefix}Hand`);
      // Bone lengths are measured fresh each IK solve (world matrices are stale at bind time,
      // and the mesh render scale changes them) — only the chain references are cached here.
      if (a && b && c) this.arms[side] = { root: a, mid: b, end: c, lenA: 0, lenB: 0 };
    }
  }

  /** World-space hand goal, or null to release. `blend` eases the grip on/off. */
  setHandTarget(side: Side, pos: V3 | null, blend = 1): void {
    if (!pos) {
      delete this.handTargets[side];
      return;
    }
    this.handTargets[side] = { pos: new THREE.Vector3(pos.x, pos.y, pos.z), blend };
  }

  /** Call after mixer.update(). `rolling` suppresses lean (the roll's velocity step would
   *  spike smoothedAccel into the clamps and fight the wrapper spin). */
  apply(vel: { x: number; z: number }, facingYaw: number, dt: number, rolling = false): void {
    // Lean/bank: smoothed acceleration decomposed into the character's local frame.
    const ax = (vel.x - this.prevVel.x) / Math.max(dt, 1e-4);
    const az = (vel.z - this.prevVel.y) / Math.max(dt, 1e-4);
    this.prevVel.set(vel.x, vel.z);
    const k = Math.min(1, dt * 8);
    this.smoothedAccel.x += (ax - this.smoothedAccel.x) * k;
    this.smoothedAccel.y += (az - this.smoothedAccel.y) * k;

    if (this.spine && !rolling) {
      const cos = Math.cos(facingYaw);
      const sin = Math.sin(facingYaw);
      // Character forward is local -Z after facingYaw; project accel into that frame.
      const fwdAccel = -(this.smoothedAccel.x * -sin + this.smoothedAccel.y * -cos);
      const sideAccel = this.smoothedAccel.x * cos + this.smoothedAccel.y * -sin;
      const lean = THREE.MathUtils.clamp(fwdAccel * -0.008, -0.21, 0.21); // ≤12°
      const bank = THREE.MathUtils.clamp(sideAccel * 0.011, -0.31, 0.31); // ≤18°
      // ABSOLUTE, never accumulated. This was `spine.rotation.x += lean` — a running integral
      // onto the Euler with nothing ever resetting it, so the decoration drifted without bound
      // and (because writing .rotation recomputes .quaternion) eventually OVERWROTE the animated
      // pose. The fix: decorate a remembered BASE. If what we find on the bone is bit-for-bit
      // what we wrote last frame, the mixer did not run for this bone and the value is our own
      // decoration — keep the old base rather than stacking on it; anything else is a fresh
      // animated pose and becomes the new base.
      if (!this.spineDecorated || this.spine.quaternion.dot(this.spineApplied) < SAME_ROTATION_DOT) {
        this.spineBase.copy(this.spine.quaternion);
      }
      LEAN_EULER.set(lean, 0, bank);
      this.spine.quaternion.copy(this.spineBase).multiply(LEAN_Q.setFromEuler(LEAN_EULER));
      this.spineApplied.copy(this.spine.quaternion);
      this.spineDecorated = true;
    } else if (this.spine && this.spineDecorated) {
      // Roll suppresses lean: hand the bone back exactly as the mixer left it, but only if it
      // still carries OUR decoration (otherwise the mixer has already moved on).
      if (this.spine.quaternion.dot(this.spineApplied) >= SAME_ROTATION_DOT) {
        this.spine.quaternion.copy(this.spineBase);
      }
      this.spineDecorated = false;
    }

    for (const side of ['left', 'right'] as const) {
      const target = this.handTargets[side];
      const chain = this.arms[side];
      if (target && chain) this.applyHandIK(chain, target.pos, target.blend);
    }
  }

  /**
   * Cheap stable two-bone: (1) set the elbow's interior angle from the analytic solve,
   * (2) aim the upper arm so the hand lands on the target, (3) slerp by blend.
   */
  private applyHandIK(chain: ArmChain, target: THREE.Vector3, blend: number): void {
    chain.root.updateWorldMatrix(true, true);
    const pRoot = V().setFromMatrixPosition(chain.root.matrixWorld);
    const pMid = V().setFromMatrixPosition(chain.mid.matrixWorld);
    const pEnd = V().setFromMatrixPosition(chain.end.matrixWorld);
    // Measure bone lengths live: correct under any mesh scale, and immune to the stale-matrix
    // trap of measuring at load (which silently zeroed the chain and disabled the solver).
    chain.lenA = pRoot.distanceTo(pMid);
    chain.lenB = pMid.distanceTo(pEnd);
    if (chain.lenA < 1e-5 || chain.lenB < 1e-5) return;
    const dist = pRoot.distanceTo(target);
    const sol = solveTwoBone(chain.lenA, chain.lenB, dist);
    const toRoot = V().subVectors(pRoot, pMid).normalize();
    const toEnd = V().subVectors(pEnd, pMid).normalize();
    const currentMid = Math.acos(THREE.MathUtils.clamp(toRoot.dot(toEnd), -1, 1));
    let bendAxis = V().crossVectors(toRoot, toEnd);
    if (bendAxis.lengthSq() < 1e-8) bendAxis = new THREE.Vector3(0, 1, 0);
    bendAxis.normalize();
    const midDelta = sol.midAngle - currentMid;
    const worldQ = new THREE.Quaternion().setFromAxisAngle(bendAxis, midDelta * blend);
    this.rotateBoneWorld(chain.mid, worldQ);

    // 2) Aim: swing the upper arm so the (new) hand direction points at the target.
    chain.root.updateWorldMatrix(true, true);
    const pEnd2 = V().setFromMatrixPosition(chain.end.matrixWorld);
    const cur = V().subVectors(pEnd2, pRoot).normalize();
    const want = V().subVectors(target, pRoot).normalize();
    const aim = new THREE.Quaternion().setFromUnitVectors(cur, want);
    const eased = new THREE.Quaternion().slerp(aim, blend);
    this.rotateBoneWorld(chain.root, eased);
  }

  private rotateBoneWorld(bone: THREE.Object3D, worldDelta: THREE.Quaternion): void {
    const parentWorld = new THREE.Quaternion();
    bone.parent?.getWorldQuaternion(parentWorld);
    const inv = parentWorld.clone().invert();
    // local' = inv(parent) * worldDelta * parent * local
    bone.quaternion.premultiply(inv.multiply(worldDelta).multiply(parentWorld));
  }
}
